
  import { mapState } from 'vuex'
  import finance from '~/mixins/common/pages/finance'
  export default {
    mixins: [finance],
    props: {
      disabled: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        steppers: 1,
        valid: true,
        form: {
          currency: null,
          network: null,
          amount: null
        },
        depositLoading: false,
        receiverBankInfo: {},
        amount: '0',
        bank: {
          min_deposit: 0,
          max_deposit: 0
        },
        loadingCryptoCurrencyAddress: false,
        delay5Seconds: false
      }
    },
    computed: {
      ...mapState({
        player: state => state.player.player,
        checkExist: state => state.themes.deposit.checkExist,
        companyCryptoWallet: state => state.themes.deposit.companyCryptoWallet,
        exchangeRateByPlayerCurrency: state => state.themes.settings.exchangeRateByPlayerCurrency,
        operationSetting: state => state.settings.operationSetting
      }),
      cryptoWalletSetting() {
        return this.companyCryptoWallet.find(el => el.currency === this.form.currency) ?? {}
      },
      disableButton() {
        if (!this.form.currency) return true
        if (!this.form.network) return true
        if (!this.form.amount) return true
        return false
      },
      cryptoExchangeRate() {
        return this.exchangeRateByPlayerCurrency?.reversed_rate
      },
      amountRules() {
        return [
          v => !!v || this.tr(this.FRONTEND_ERROR_CODES.required),
          v =>
            (v && parseFloat(this.form.amount) >= parseFloat(this.bank.deposit_min)) ||
            `${this.tr(this.FRONTEND_ERROR_CODES.minDepositIs)} ${this.formatCurrencyJS(
              this.bank.deposit_min
            )}`,
          v =>
            (v && parseFloat(this.form.amount) <= parseFloat(this.bank.deposit_max)) ||
            `${this.tr(this.FRONTEND_ERROR_CODES.maxDepositIs)} ${this.formatCurrencyJS(
              this.bank.deposit_max
            )}`,
          v =>
            this.decimalRule(v?.replace(/,/g, '')) ||
            this.tr(this.FRONTEND_ERROR_CODES.decimalIsTwoDigit)
        ]
      },
      amountOfUSDTRules() {
        return [
          v => !!v || this.tr(this.FRONTEND_ERROR_CODES.required),
          v =>
            (v && parseFloat(this.form.amount) >= parseFloat(this.operationSetting.deposit_min)) ||
            `${this.tr(this.FRONTEND_ERROR_CODES.minDepositIs)} ${this.formatCurrencyJS(
              this.operationSetting.deposit_min
            )}`,
          v =>
            (v && parseFloat(this.form.amount) <= parseFloat(this.operationSetting.deposit_max)) ||
            `${this.tr(this.FRONTEND_ERROR_CODES.maxDepositIs)} ${this.formatCurrencyJS(
              this.operationSetting.deposit_max
            )}`,
          v => this.decimalRule(v) || this.tr(this.FRONTEND_ERROR_CODES.decimalIsTwoDigit)
        ]
      },
      networkList() {
        return this.companyCryptoWallet
          .filter(item => item.currency === 'USDT') // filter USDT currency
          .map(item => item.network) // map to get only the network
      },
      actualDeposit() {
        const value = parseFloat(this.cryptoExchangeRate) * parseFloat(this.form.amount)
        return value || '0'
      }
    },
    watch: {
      checkExist(value) {
        if (value) {
          this.isExist()
        }
      },
      'form.currency'(value) {
        this.bank.deposit_max = parseFloat(this.cryptoWalletSetting?.max_deposit)
        this.bank.deposit_min = parseFloat(this.cryptoWalletSetting?.min_deposit)
      },
      companyCryptoWallet(value) {
        if (!this.form.currency && value && value.length) {
          this.checkWalletAndSetDefault(value)
        }
      }
    },
    async mounted() {
      try {
        if (this.$cookie.get('player')?.currency !== 'USDT')
          await this.$store.dispatch('themes/settings/getExchangeRateByPlayerCurrency', {
            currency: this.$cookie.get('player')?.currency
          })
        this.loadingCryptoCurrencyAddress = true
        this.isExist()
        await this.$store.dispatch('themes/deposit/getCompanyCryptoWallet')
      } catch (error) {
      } finally {
        this.checkWalletAndSetDefault()
        this.loadingCryptoCurrencyAddress = false
      }
    },
    methods: {
      isExist() {
        if (
          this.checkExist &&
          this.checkExist.is_exist === true &&
          this.checkExist.payment_provider_code === 'crypto'
        ) {
          this.receiverBankInfo = this.checkExist
          if (this.checkExist?.created_at && this.checkExist.deposit_status === 1) {
            this.steppers = 2
          } else if ([2, 3, 4].includes(this.checkExist.deposit_status)) {
            this.steppers = 3
          }
        } else {
          this.steppers = 1
        }
      },
      async firstForm() {
        if (this.$refs.firstForm.validate()) {
          // Track Events if not first deposit
          await this.trackDepositEvent(this.checkExist.sequence, this.form.amount)

          this.depositLoading = true
          const response = await this.$store.dispatch('themes/deposit/depositCrypto', this.form)
          if (response) {
            this.receiverBankInfo = response
            this.steppers = 2
            this.depositLoading = false
            this.$emit('cryptoTransferConfirm', true)
            this.delay5Seconds = true
            setTimeout(() => {
              this.delay5Seconds = false
            }, 5000)
          }
          this.depositLoading = false
        }
      },
      async onConfirmDeposit() {
        this.depositLoading = true
        const response = await this.$store.dispatch(
          'themes/deposit/confirm',
          this.receiverBankInfo.id
        )
        if (response) {
          this.steppers++
        }
        this.depositLoading = false
      },
      async onRevokeDeposit() {
        this.depositLoading = true
        const response = await this.$store.dispatch(
          'themes/deposit/revoke',
          this.receiverBankInfo.id
        )
        if (response) {
          this.steppers--
          this.$emit('cryptoTransferConfirm', false)
          this.receiverBankInfo = {}
          this.$refs.firstForm.resetValidation()
        }
        this.depositLoading = false
      },
      clearForm() {
        this.$refs.firstForm.resetValidation()
        this.form = {}
      },
      findSmallestNumber() {
        return this.bank.deposit_min / this.cryptoExchangeRate
      },
      findLargestNumber() {
        return Math.floor(this.bank.deposit_max / this.cryptoExchangeRate)
      },
      checkWalletAndSetDefault(companyCryptoWalletList = this.companyCryptoWallet) {
        if (companyCryptoWalletList.length) {
          const currencyData = companyCryptoWalletList[0]
          this.form.currency = currencyData.currency
          this.form.network = currencyData.network
        }
      }
    }
  }
